import React from 'react';
import PropTypes from 'prop-types';

import { DRAG_TYPES } from '@techchange/dnd';
import { useLanguageContext } from '@techchange/components';

import { RESOURCE_TYPES } from '../../constants';
import { isSafeUrl } from '../../utils/urls';

import './ContentGridDragPreview.scss';

const propTypes = {

	/**
	 * The dragged item to preview.
	 */
	item: PropTypes.shape({

		/**
		 * The drag-and-drop type of the dragged item,
		 * provided by react-dnd.
		 */
		type: PropTypes.string.isRequired,

		/**
		 * The original data for the dragged item.
		 */
		data: PropTypes.shape({

			/**
			 * The item type.
			 */
			type: PropTypes.oneOf([
				RESOURCE_TYPES.EMBED,
				RESOURCE_TYPES.FORM,
				RESOURCE_TYPES.IMAGE,
				RESOURCE_TYPES.POLL,
				RESOURCE_TYPES.QUIZ,
				RESOURCE_TYPES.SLIDE,
				RESOURCE_TYPES.VIDEO,
			]).isRequired,

			/**
			 * The item title.
			 */
			title: PropTypes.string.isRequired,

			/**
			 * The list of multi-language contents for the resource.
			 */
			resource_contents: PropTypes.shape({

				/**
				 * The content language.
				 */
				language: PropTypes.string.isRequired,

				/**
				 * The content title.
				 */
				title: PropTypes.string,

				/**
				 * Additional data for the content.
				 */
				data: PropTypes.shape({}),

			}).isRequired,

		}).isRequired,

	}),

};

const renderImagePreview = (resource, resourceContent) => (
	<img
		src={resourceContent?.data?.src}
		alt={resourceContent?.data?.alt || 'Image'} />
);

const renderVideoPreview = (resource, resourceContent) => {

	const hasTitle = resource.show_title && Boolean(resourceContent?.title);

	return (
		<div>
			{hasTitle &&
				<span className="title">
					<h5>
						{resourceContent?.title}
					</h5>
				</span>
			}
			{isSafeUrl(resourceContent?.data?.src) &&
				<iframe
					title={resourceContent?.title}
					src={resourceContent?.data?.src} />
			}
		</div>
	);

};

const renderDefaultPreview = (resource, resourceContent) => (
	<span className="title">
		<h5>
			{resourceContent?.title}
		</h5>
	</span>
);

/**
 * Renders the drag preview for a form editor item.
 *
 * @param   {object} props  The component props.
 *
 * @returns {node}          The rendered drag preview for the item.
 */
const ContentGridDragPreview = ({ item }) => {

	const { chooseContentFromLanguage } = useLanguageContext();

	if (item.type?.startsWith(DRAG_TYPES.SUBMODULE_CONTENT)) {

		let preview = null;

		if (item.data) {

			// Get the translated content for the resource
			const resourceContent = chooseContentFromLanguage(item.data.resource_contents);

			if (item.data.type === RESOURCE_TYPES.IMAGE) {

				preview = renderImagePreview(item.data, resourceContent);

			} else if (item.data.type === RESOURCE_TYPES.VIDEO) {

				preview = renderVideoPreview(item.data, resourceContent);

			} else {

				preview = renderDefaultPreview(item.data, resourceContent);

			}

		}

		return (
			<div
				className={`
					tc-content-grid-drag-preview
					tc-content-grid-drag-preview-${item.data.type}
				`}>
				{preview}
			</div>
		);


	}

	console.warn(`The drag type has no support for a drag preview: ${item.type}`);
	return null;

};

ContentGridDragPreview.propTypes = propTypes;

export default ContentGridDragPreview;
